import React, { useCallback, useState } from 'react';
import { Alert, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Button, TextInput as PaperTextInput, useTheme } from 'react-native-paper';
import BaseBottomSheet from './BaseBottomSheet';
import TruckDropdown from './TruckDropdown';
import { useAuth } from '../context/AuthContext';
import ExpenseService from '../services/expenseService';
import { CreateExpenseData } from '../types/Expense';
import { Truck } from '../types/Truck';
import { ErrorLogger } from '../utils/errorLogger';

export interface ExpenseData {
  amount: string;
  description: string;
  category?: string;
  truck?: Truck;
}

interface AddExpenseBottomSheetProps {
  visible: boolean;
  onSave?: (expense: ExpenseData) => void; // Optional callback for external handling
  onClose: () => void;
  onExpenseCreated?: () => void; // Callback when expense is successfully created
  onSuccess?: () => void; // Callback to show success message
}

const styles = StyleSheet.create({
  scrollView: {
    flexShrink: 1,
  },
  scrollContent: {
    padding: 20,
  },
  fieldContainer: {
    marginBottom: 8,
  },
  paperInput: {},
  currencyIcon: {
    fontFamily: 'Inter-Medium',
    fontSize: 16,
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 6,
    padding: 14,
  },
  switchLabel: {
    fontFamily: 'Inter-Medium',
    fontSize: 16,
  },
  buttonContainer: {
    padding: 16,
    paddingBottom: 16,
  },
});

const AddExpenseBottomSheet: React.FC<AddExpenseBottomSheetProps> = ({
  visible,
  onSave,
  onClose,
  onExpenseCreated,
  onSuccess,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const CurrencyIcon = () => <Text style={[styles.currencyIcon, { color: theme.colors.onSurfaceVariant }]}>UGX</Text>;
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [selectedTruck, setSelectedTruck] = useState<Truck | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(async () => {
    if (!amount.trim()) {
      Alert.alert('Error', 'Please enter an amount');
      return;
    }

    if (!user?.uid) {
      Alert.alert('Error', 'You must be logged in to add an expense');
      return;
    }

    const numericAmount = parseFloat(amount.trim());
    if (isNaN(numericAmount) || numericAmount <= 0) {
      Alert.alert('Error', 'Please enter a valid amount');
      return;
    }

    try {
      setSaving(true);

      const expenseData: CreateExpenseData = {
        amount: numericAmount,
        description: description.trim() || 'Expense',
        category: category.trim() || undefined,
        truck: selectedTruck || undefined,
      };

      // Save to Firestore
      await ExpenseService.createExpense(user.uid, expenseData);

      // Call legacy callback if provided (for backward compatibility)
      onSave?.({
        amount: amount.trim(),
        description: description.trim(),
        category: category.trim(),
        truck: selectedTruck || undefined,
      });

      // Reset form
      setAmount('');
      setDescription('');
      setCategory('');
      setSelectedTruck(null);

      // Notify parent component
      onExpenseCreated?.();

      // Close the modal first
      onClose();

      // Show success message
      onSuccess?.();
    } catch (error) {
      ErrorLogger.logError(error as any, 'AddExpenseBottomSheet.handleSave');
      Alert.alert('Error', 'Failed to save expense. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [
    amount,
    description,
    category,
    user,
    onSave,
    onExpenseCreated,
    onClose,
    onSuccess,
    selectedTruck,
  ]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <BaseBottomSheet
      visible={visible}
      onClose={handleClose}
      title="Add Expense"
      height={0.5}
    >
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* Truck Selection */}
        <View style={styles.fieldContainer}>
          <TruckDropdown
            selectedTruck={selectedTruck}
            onTruckSelect={setSelectedTruck}
            label="Truck"
            placeholder="Select a truck (optional)"
            required={false}
          />
        </View>
        {/* Amount Field */}
        <View style={styles.fieldContainer}>
          <PaperTextInput
            mode="flat"
            label="Amount"
            value={amount}
            onChangeText={setAmount}
            placeholder="0"
            keyboardType="numeric"
            left={
              <PaperTextInput.Icon
                icon={CurrencyIcon}
              />
            }
            style={[styles.paperInput, { backgroundColor: theme.colors.surfaceVariant }]}
            testID="amount-input"
          />
        </View>

        {/* Description Field */}
        <View style={styles.fieldContainer}>
          <PaperTextInput
            mode="flat"
            label="Description"
            value={description}
            onChangeText={setDescription}
            placeholder="Enter expense description..."
            style={[styles.paperInput, { backgroundColor: theme.colors.surfaceVariant }]}
            testID="description-input"
          />
        </View>
      </ScrollView>

      {/* Save Button */}
      <View style={styles.buttonContainer}>
        <Button
          disabled={!amount.trim() || saving}
          mode="contained"
          onPress={handleSave}
          loading={saving}
          testID="save-button"
        >
          {saving ? 'Saving...' : 'Save Expense'}
        </Button>
      </View>
    </BaseBottomSheet>
  );
};

export default AddExpenseBottomSheet;
